import { useQuery } from '@tanstack/react-query';
import { Negotiation } from '@interfaces/models/negotiation';
import { CTAProductQueryKeys, NegotiationKeys } from '@enums/react-query-keys';
import ProductService from '@services/product-service';
import logger from '@helpers/utils/logger/client';
import { useNegotiation } from '@context/negotiation.context';
import { Product } from '@interfaces/models/product';
import MmaoService from '@services/mmao-service';
import NegotiationsService from '@services/negotiations-service';

export const useActiveNegotiation = ({ productId, enabled }: { productId: Product['id']; enabled: boolean }) => {
  const { setNegotiation } = useNegotiation();

  const { data, refetch: fetchLatestNegotiation } = useQuery<Negotiation>({
    queryKey: [NegotiationKeys.ACTIVE_NEGOTIATION, productId],
    queryFn: () => ProductService.getActiveNegotiation(productId, true),
    enabled,
    onSuccess: (data) => {
      setNegotiation(data);
    },
    onError: (error) => {
      logger.error(error, 'Error occurred while fetching active negotiation');
    },
  });

  return { data, fetchLatestNegotiation };
};

export const useNegotiationsByProduct = ({
  productId,
  enabled,
  onSettled,
}: {
  productId: Product['id'];
  enabled: boolean;
  onSettled?: () => void;
}) => {
  const { setNegotiationList } = useNegotiation();

  const { data, refetch } = useQuery<Negotiation[]>({
    queryKey: [NegotiationKeys.NEGOTIATIONS_BY_PRODUCT, productId],
    queryFn: () => MmaoService.getNegotiationsByProduct(productId),
    enabled: enabled,
    onSuccess: (data) => {
      setNegotiationList(data);
    },
    onSettled,
    onError: (error) => {
      logger.error(error, 'Error occurred while fetching active negotiation');
    },
  });
  return { data, refetch };
};

export const useCurrentNegotiation = ({
  negotiationId,
  enabled,
  onQuery,
  onSuccess,
}: {
  negotiationId: Negotiation['id'];
  enabled: boolean;
  onQuery: () => void;
  onSuccess: () => void;
}) => {
  const { setNegotiation, negotiation } = useNegotiation();

  useQuery<Negotiation>({
    queryKey: [CTAProductQueryKeys.NEGOTIATION, negotiationId],
    queryFn: () => {
      onQuery();
      return NegotiationsService.getNegotiation(negotiationId);
    },
    onSuccess: (data: Negotiation | undefined) => {
      onSuccess();
      setNegotiation(data);
    },
    onError: (error) => {
      logger.error(error, 'Server responded with an error while fetching active negotiation: ');
    },
    initialData: negotiation,
    enabled,
  });
};
